import React, { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Undo2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";

/**
 * RowActions — Edit / Delete / Reverse cell for editable operational tables.
 *
 * Renders an "Actions" cell with three optional buttons:
 *   - Edit: opens a modal populated with existing row data via renderEditForm(rowState, setRowState).
 *           On save calls PATCH `${basePath}/${row.id}` with rowState + correction_reason.
 *   - Delete: opens an audit-warning confirmation and calls DELETE `${basePath}/${row.id}`.
 *   - Reverse: for financial rows — opens a confirmation and POSTs to `${basePath}/${row.id}/reverse`.
 *
 * canWrite AND role controls visibility. The Manager role has :write grants but
 * is blocked at backend middleware from PATCH/PUT/DELETE — so the FE also
 * short-circuits to a read-only badge to mirror what the backend will enforce.
 * Reverse follows the same rule (Sales & Cash reversal is a mutation).
 * All actions surface a correction reason field that the backend stores in
 * correction_history / audit log.
 */
const CREATE_ONLY_ROLES = new Set(["manager"]);

export function RowActions({
  row,
  canWrite,
  basePath,           // e.g. "/poultry/daily"
  resource,           // e.g. "daily-capture"  (for data-testid)
  renderEditForm,     // (state, setState) => JSX rendering the fields for edit; omit to disable Edit
  editableFields,     // array of keys to seed the edit state from the row (default: all row keys)
  actions = ["edit", "delete"],  // subset of ["edit","delete","reverse"]
  onChanged,          // callback after any successful mutation
  destructiveLabel = "Delete",   // Delete vs Void wording
  reverseLabel = "Reverse",
}) {
  const { role } = usePermissions();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  // Manager role is CREATE-only by directive & middleware; short-circuit to read-only.
  if (!canWrite || CREATE_ONLY_ROLES.has(role)) {
    return (
      <span className="text-xs text-muted-foreground italic" data-testid={`row-actions-readonly-${row.id}`}>
        Read-only
      </span>
    );
  }
  return (
    <div className="flex items-center justify-end gap-1.5" data-testid={`row-actions-${row.id}`}>
      {actions.includes("edit") && renderEditForm && (
        <>
          <Button size="sm" variant="outline" className="h-8 px-2"
            onClick={() => setEditOpen(true)} data-testid={`row-edit-${row.id}`}>
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
          {editOpen && (
            <EditRowModal
              row={row} basePath={basePath} resource={resource}
              editableFields={editableFields} renderEditForm={renderEditForm}
              onClose={() => setEditOpen(false)}
              onSaved={() => { setEditOpen(false); onChanged?.(); }}
            />
          )}
        </>
      )}
      {actions.includes("reverse") && (
        <>
          <Button size="sm" variant="outline" className="h-8 px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => setReverseOpen(true)} data-testid={`row-reverse-${row.id}`}>
            <Undo2 className="h-3.5 w-3.5 mr-1" /> {reverseLabel}
          </Button>
          {reverseOpen && (
            <ConfirmDialog
              title={`${reverseLabel} this transaction?`}
              description="A compensating entry will be created; the original record is preserved and Poultry360 quantities will be restored automatically."
              confirmLabel={reverseLabel}
              destructive={false}
              basePath={`${basePath}/${row.id}/reverse`}
              method="POST"
              resource={resource}
              onClose={() => setReverseOpen(false)}
              onDone={() => { setReverseOpen(false); onChanged?.(); }}
            />
          )}
        </>
      )}
      {actions.includes("delete") && (
        <>
          <Button size="sm" variant="outline" className="h-8 px-2 border-destructive/40 text-destructive hover:bg-destructive/10"
            onClick={() => setDeleteOpen(true)} data-testid={`row-delete-${row.id}`}>
            <Trash2 className="h-3.5 w-3.5 mr-1" /> {destructiveLabel}
          </Button>
          {deleteOpen && (
            <ConfirmDialog
              title={`${destructiveLabel} this record?`}
              description="The record will be soft-deleted and the action will be recorded in the audit trail. Related quantities (birds, feed balance) will be reconciled automatically."
              confirmLabel={destructiveLabel}
              destructive
              basePath={`${basePath}/${row.id}`}
              method="DELETE"
              resource={resource}
              onClose={() => setDeleteOpen(false)}
              onDone={() => { setDeleteOpen(false); onChanged?.(); }}
            />
          )}
        </>
      )}
    </div>
  );
}

function EditRowModal({ row, basePath, resource, editableFields, renderEditForm, onClose, onSaved }) {
  const initial = editableFields
    ? Object.fromEntries(editableFields.map(k => [k, row[k] ?? ""]))
    : Object.fromEntries(Object.entries(row).filter(([k]) => !["_id", "id", "created_at", "updated_at", "correction_history", "captured_by", "captured_by_role"].includes(k)));
  const [state, setState] = useState(initial);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit() {
    setSaving(true);
    try {
      const patch = { ...state };
      if (reason.trim()) patch.correction_reason = reason.trim();
      await api.patch(`${basePath}/${row.id}`, patch);
      toast.success("Correction saved. Audit trail updated.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Correction failed");
    } finally { setSaving(false); }
  }
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={`edit-modal-${resource}`}>
        <DialogHeader>
          <DialogTitle>Edit {resource.replace(/-/g, " ")}</DialogTitle>
          <p className="text-xs text-muted-foreground">Only correct fields that were entered in error. Original values are retained in the audit trail.</p>
        </DialogHeader>
        <div className="space-y-3">
          {renderEditForm(state, setState)}
          <div className="pt-2 border-t border-border">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason for correction</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Original mortality count included two birds that were later found alive."
              data-testid={`edit-reason-${resource}`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid={`edit-save-${resource}`}>
            {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>) : "Save correction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({ title, description, confirmLabel, destructive, basePath, method, resource, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  async function run() {
    setBusy(true);
    try {
      if (method === "DELETE") {
        await api.delete(basePath, { data: { reason } });
      } else {
        await api.post(basePath, { reason });
      }
      toast.success(`${confirmLabel} recorded in audit trail.`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${confirmLabel} failed`);
    } finally { setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={`confirm-${resource}`}>
        <DialogHeader>
          <DialogTitle className={destructive ? "text-destructive" : "text-amber-700"}>{title}</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </DialogHeader>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason (optional)</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Record why this correction is needed."
            data-testid={`confirm-reason-${resource}`} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            className={destructive ? "bg-destructive hover:bg-destructive/90" : "bg-amber-600 hover:bg-amber-700"}
            onClick={run} disabled={busy} data-testid={`confirm-btn-${resource}`}>
            {busy ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Working…</>) : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
